import { Body } from '@/layout'
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import BackIcon from '@/assets/back-icon.svg?react'
import { CalendarIcon, ArrowDropDownIcon } from '@mui/x-date-pickers'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { useDateProduct } from '@/hooks/Entity/useHelper'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import KeywordSocialMediaAnalytics from '@/modules/Analytic/KeywordSocialMediaAnalytics'
import KeywordOnlineMediaAnalytics from '@/modules/Analytic/KeywordOnlineMediaAnalytics'

const fontSize = { xs: '14px', sm: '16px' }
const fontButton = { xs: '8px', sm: '14px' }

const timeOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'sevenDays' },
  { label: 'Last 30 Days', value: 'thirtyDays' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
]

interface ProductPrice {
  payload: {
    length: number
    price: number
    currency: string
  }
}

interface UseProductPriceReturn {
  data: ProductPrice | null
}

const KeywordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const keyword = searchParams.get('keyword')
  const theme = useTheme()
  const isScreenSmUp = useMediaQuery(theme.breakpoints.up('sm'))

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const popOverId = open ? 'date-range-popOver' : undefined
  const [startDateLocal, setStartDateLocal] = useState(null)
  const [endDateLocal, setEndDateLocal] = useState(null)
  const { endDate, startDate, setStartDate, setEndDate } = useComparisonStore()

  const [value, setValue] = useState<string>('1')

  const { data: payloadDate } = useDateProduct() as UseProductPriceReturn

  const minDate = useMemo(() => {
    return new Date(payloadDate?.payload[0]?.startDate)
  }, [payloadDate])

  const maxDate = useMemo(() => {
    return new Date(payloadDate?.payload[payloadDate?.payload?.length - 1].endDate)
  }, [payloadDate])

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue.toString())
  }

  const handleCloseDateRange = () => {
    setAnchorEl(null)
  }

  const handleChangeDateRange = (dates) => {
    const [start, end] = dates
    setStartDateLocal(start)
    setEndDateLocal(end)
  }

  const handleClickTimeFrame = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const confirmDateRange = (startDate: Date, endDate: Date) => {
    setStartDate(moment(startDate).format('YYYY-MM-DD'))
    setEndDate(moment(endDate).format('YYYY-MM-DD'))
    handleCloseDateRange()
  }

  const handleClickTimeOption = (option: string) => {
    const today = new Date()
    switch (option) {
      case 'today':
        confirmDateRange(today, today)
        break
      case 'yesterday': {
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        confirmDateRange(yesterday, yesterday)
        break
      }
      case 'sevenDays': {
        const oneWeekAgo = new Date(today)
        oneWeekAgo.setDate(today.getDate() - 7)
        confirmDateRange(oneWeekAgo, today)
        break
      }
      case 'thirtyDays': {
        const thirtyDaysAgo = new Date(today)
        thirtyDaysAgo.setDate(today.getDate() - 30)
        confirmDateRange(thirtyDaysAgo, today)
        break
      }
      case 'thisMonth': {
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        confirmDateRange(firstDayOfMonth, today)
        break
      }
      case 'lastMonth': {
        const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const lastDayOfLastMonth = new Date(firstDayOfCurrentMonth.getTime() - 1 * 24 * 60 * 60 * 1000)
        confirmDateRange(firstDayOfLastMonth, lastDayOfLastMonth)
        break
      }
      default:
        throw new Error('Time option is not valid.')
    }
  }

  const CustomInputWithButton = () => (
    <Button
      disabled={startDateLocal === null || endDateLocal === null}
      onClick={() => {
        setStartDate(moment(startDateLocal).format('YYYY-MM-DD'))
        setEndDate(moment(endDateLocal).format('YYYY-MM-DD'))
        setAnchorEl(null)
      }}
    >
      Confirm
    </Button>
  )

  return (
    <Body sticky={false}>
      <Box sx={{ backgroundColor: '#262626' }}>
        <Container maxWidth="xl">
          <Grid
            xs={12}
            container
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={{ xs: 'column-reverse', sm: 'row' }}
            sx={{ fontSize: '12px', py: { xs: 0.5, sm: 2 } }}
          >
            <Grid item display="flex" gap={3}>
              <IconButton sx={{ display: { xs: 'none', sm: 'block' } }} onClick={() => navigate(-1)}>
                <BackIcon />
              </IconButton>
              <div>
                <Typography sx={{ color: 'white', fontSize: fontSize }}>
                  <Typography sx={{ color: '#dfca72', fontWeight: 600, fontSize: fontSize }} component="span">
                    ~{keyword}
                  </Typography>{' '}
                </Typography>
                <Typography sx={{ color: 'white', fontSize: fontSize }}>Keyword</Typography>
              </div>
            </Grid>
            <Grid item>
              <div>
                <Button
                  aria-describedby={popOverId}
                  type="button"
                  onClick={handleClickTimeFrame}
                  variant="text"
                  disableRipple
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    height: { xs: 25, sm: 40 },
                    fontSize: fontButton,
                    borderRadius: 2,
                    textTransform: 'unset',
                    ':hover': { bgcolor: 'white' },
                  }}
                >
                  <InputAdornment position="start">
                    <CalendarIcon />
                  </InputAdornment>
                  <div style={{ overflow: 'auto', width: isScreenSmUp ? 'auto' : '80px' }}>
                    <Typography fontSize={{ xs: 10, sm: 14 }} textOverflow="ellipsis" noWrap>
                      {moment(startDate).format('DD MMM YYYY')} - {moment(endDate).format('DD MMM YYYY')}
                    </Typography>
                  </div>
                  <InputAdornment position="end">
                    <ArrowDropDownIcon />
                  </InputAdornment>
                </Button>
                <Popover
                  id={popOverId}
                  open={open}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  onClose={handleCloseDateRange}
                >
                  <Stack direction={isScreenSmUp ? 'row' : 'column'}>
                    {isScreenSmUp ? (
                      <List>
                        {timeOptions.map((option) => (
                          <ListItem disablePadding key={option.value}>
                            <ListItemButton dense onClick={() => handleClickTimeOption(option.value)}>
                              <ListItemText primaryTypographyProps={{ fontSize: '12px' }}>{option.label}</ListItemText>
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Stack direction="row" gap={1} flexWrap="wrap" padding={1}>
                        {timeOptions.map((option, index) => (
                          <>
                            <Button sx={{ textTransform: 'none' }} onClick={() => handleClickTimeOption(option.value)}>
                              {option.label}
                            </Button>
                            {index < timeOptions.length - 1 && (
                              <Divider orientation="vertical" variant="middle" flexItem />
                            )}
                          </>
                        ))}
                      </Stack>
                    )}
                    <Box overflow="scroll">
                      <DatePicker
                        selected={startDateLocal}
                        onChange={handleChangeDateRange}
                        startDate={startDateLocal}
                        endDate={endDateLocal}
                        minDate={minDate}
                        maxDate={maxDate}
                        selectsRange
                        inline
                      >
                        <CustomInputWithButton />
                      </DatePicker>
                    </Box>
                  </Stack>
                </Popover>
              </div>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container maxWidth="xl">
        <TabContext value={value || '1'}>
          <TabList
            TabIndicatorProps={{ style: { display: 'none' } }}
            variant="fullWidth"
            onChange={handleChange}
            centered
            sx={{ py: 3 }}
          >
            <Tab
              value="1"
              label="Social Media"
              sx={{
                borderRadius: 2,
                color: value === '1' ? '#FFFFFF' : 'black',
                backgroundColor: value === '1' ? 'rgba(56, 42, 2, 1)' : 'white',
                '&.Mui-selected': {
                  color: 'white',
                },
              }}
            />
            <Tab
              value="2"
              label="Online Media"
              sx={{
                borderRadius: 2,
                color: value === '2' ? '#FFFFFF' : 'black',
                backgroundColor: value === '2' ? 'rgba(56, 42, 2, 1)' : 'white',
                '&.Mui-selected': {
                  color: 'white',
                },
              }}
            />
          </TabList>
          <TabPanel value="1" sx={{ p: 0 }}>
            <KeywordSocialMediaAnalytics />
            {/* <div>test</div> */}
          </TabPanel>
          <TabPanel value="2" sx={{ p: 0 }}>
            <KeywordOnlineMediaAnalytics />
          </TabPanel>
        </TabContext>
      </Container>
    </Body>
  )
}

export default KeywordPage
